"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type PropsWithChildren,
} from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/auth-context";
import {
  getVisibilitySettings,
  DEFAULT_PAGES,
  DEFAULT_SECTIONS,
  type VisibilitySettings,
  type RoleVisibility,
} from "@/services/visibility-settings";

/* ------------------------------------------------------------------ */
/*  Context shape                                                      */
/* ------------------------------------------------------------------ */

interface VisibilityContextType {
  settings: VisibilitySettings;
  loading: boolean;
  /** True when `pageKey` should be shown for the current user */
  isPageVisible: (pageKey: string) => boolean;
  /** True when `sectionKey` inside `pageKey` should be shown */
  isSectionVisible: (pageKey: string, sectionKey: string) => boolean;
  /** Force re-fetch (e.g. after admin save) */
  refetch: () => Promise<void>;
}

const VisibilityContext = createContext<VisibilityContextType | null>(null);

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function accountTypeKey(
  accountType: string | undefined
): keyof RoleVisibility {
  if (!accountType) return "consumer";
  const lower = accountType.toLowerCase();
  if (lower === "superadmin") return "superadmin";
  if (lower === "admin") return "admin";
  if (lower === "creator" || lower === "content_creator" || lower === "artist" || lower === "professional") return "creator";
  return "consumer";
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

const SUPER_ADMIN_EMAILS = ["breakinbreadswe@gmail.com"];

function isSuperAdminUser(
  email?: string | null,
  roles?: string[],
  accountType?: string
): boolean {
  if (SUPER_ADMIN_EMAILS.includes((email || "").toLowerCase().trim())) return true;
  if ((roles || []).some(r => r.toLowerCase() === "superadmin" || r.toLowerCase() === "super admin")) return true;
  if ((accountType || "").toLowerCase() === "superadmin") return true;
  return false;
}

export function VisibilityProvider({ children }: PropsWithChildren) {
  const { user, userProfile } = useAuth();
  const [settings, setSettings] = useState<VisibilitySettings>({
    pages: DEFAULT_PAGES,
    sections: DEFAULT_SECTIONS,
  });
  const [loading, setLoading] = useState(true);

  // Initial fetch + real-time listener
  useEffect(() => {
    let cancelled = false;

    // Initial load
    getVisibilitySettings().then((data) => {
      if (!cancelled) {
        setSettings(data);
        setLoading(false);
      }
    });

    // Real-time listener
    const unsubscribe = onSnapshot(
      doc(db, "siteSettings", "page-visibility"),
      (snapshot) => {
        if (snapshot.exists() && !cancelled) {
          const data = snapshot.data() as VisibilitySettings;
          // Merge with defaults in case new pages added
          const existingPageKeys = new Set(data.pages.map((p) => p.pageKey));
          const mergedPages = [...data.pages];
          for (const def of DEFAULT_PAGES) {
            if (!existingPageKeys.has(def.pageKey)) mergedPages.push({ ...def });
          }

          const existingSectionKeys = new Set(
            data.sections.map((s) => `${s.pageKey}::${s.sectionKey}`)
          );
          const mergedSections = [...data.sections];
          for (const def of DEFAULT_SECTIONS) {
            if (!existingSectionKeys.has(`${def.pageKey}::${def.sectionKey}`)) {
              mergedSections.push({ ...def, order: mergedSections.length });
            }
          }

          setSettings({
            ...data,
            pages: mergedPages,
            sections: mergedSections.sort((a, b) => a.order - b.order),
          });
          setLoading(false);
        }
      },
      (error) => {
        console.error("Visibility settings listener error:", error);
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const refetch = useCallback(async () => {
    const data = await getVisibilitySettings();
    setSettings(data);
  }, []);

  /* ---- Visibility checks ---- */

  const isPageVisible = useCallback(
    (pageKey: string): boolean => {
      // SuperAdmin always sees everything — check email, roles, and accountType (matches isSuperAdmin in sidebar-nav)
      if (isSuperAdminUser(user?.email, userProfile?.roles, userProfile?.accountType)) return true;

      const rule = settings.pages.find((p) => p.pageKey === pageKey);
      if (!rule) return true; // No rule → default visible

      const key = accountTypeKey(userProfile?.accountType);
      return rule.visibleTo[key] ?? true;
    },
    [settings.pages, user, userProfile]
  );

  const isSectionVisible = useCallback(
    (pageKey: string, sectionKey: string): boolean => {
      if (isSuperAdminUser(user?.email, userProfile?.roles, userProfile?.accountType)) return true;

      const rule = settings.sections.find(
        (s) => s.pageKey === pageKey && s.sectionKey === sectionKey
      );
      if (!rule) return true;

      const key = accountTypeKey(userProfile?.accountType);
      return rule.visibleTo[key] ?? true;
    },
    [settings.sections, user, userProfile]
  );

  return (
    <VisibilityContext.Provider
      value={{ settings, loading, isPageVisible, isSectionVisible, refetch }}
    >
      {children}
    </VisibilityContext.Provider>
  );
}

export function useVisibility() {
  const ctx = useContext(VisibilityContext);
  if (!ctx)
    throw new Error("useVisibility must be used within VisibilityProvider");
  return ctx;
}
